import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { PropsWithChildren } from 'react'
import type firebase from 'firebase/app'
import {
  getCurrentFirebaseUser,
  getUserActiveSessionId,
  hasFirebaseConfig,
  logoutCurrentUser,
  subscribeToActiveSession,
  subscribeToAuthState,
} from '@services/firebase'
import {
  AuthSessionContext,
  type AuthSessionContextValue,
} from '@features/auth/context/AuthSessionContext'
import {
  createInitialAuthSessionState,
  deriveAuthSessionState,
  getActiveAuthSessionState,
  getFailedAuthSessionState,
  getReplacedAuthSessionState,
  getResolvingAuthSessionState,
  getSignedOutAuthSessionState,
  type AuthSessionLifecycleState,
} from './auth-session-state'
import { traceLearningPerformance } from '@shared/learning/learning-performance-trace'

export function AuthSessionProvider({ children }: PropsWithChildren) {
  const [authUser, setAuthUser] = useState<firebase.User | null>(() =>
    getCurrentFirebaseUser(),
  )
  const [user, setUser] = useState<firebase.User | null>(null)
  const [authSessionRevision, setAuthSessionRevision] = useState(0)
  const sessionVerificationRevisionRef = useRef(0)
  const verifiedUserIdRef = useRef<string | null>(null)
  const [sessionState, setSessionState] = useState<AuthSessionLifecycleState>(() =>
    createInitialAuthSessionState(Boolean(authUser)),
  )

  useEffect(() => {
    if (!hasFirebaseConfig()) {
      return
    }

    return subscribeToAuthState((nextUser) => {
      sessionVerificationRevisionRef.current += 1

      if (nextUser) {
        setSessionState((currentState) => {
          if (
            currentState.status === 'active' &&
            verifiedUserIdRef.current === nextUser.uid
          ) {
            return currentState
          }

          return getResolvingAuthSessionState()
        })
      } else {
        verifiedUserIdRef.current = null
        setUser(null)
        setSessionState((currentState) =>
          getSignedOutAuthSessionState(currentState.signOutReason),
        )
      }

      setAuthUser(nextUser)
      setAuthSessionRevision((revision) => revision + 1)
    })
  }, [])

  useEffect(() => {
    if (!hasFirebaseConfig() || !authUser) {
      return
    }

    if (verifiedUserIdRef.current === authUser.uid) {
      return
    }

    let isMounted = true
    const verificationRevision = sessionVerificationRevisionRef.current

    void traceLearningPerformance({
      event: 'auth.sessionClaim.initial',
      task: () => getUserActiveSessionId(authUser, true),
    })
      .then((sessionId) => {
        if (!isMounted || sessionVerificationRevisionRef.current !== verificationRevision) {
          return
        }

        if (sessionId) {
          verifiedUserIdRef.current = authUser.uid
          setUser(authUser)
          setSessionState(getActiveAuthSessionState())
          return
        }

        verifiedUserIdRef.current = null
        setUser(null)
        setSessionState(getSignedOutAuthSessionState())
      })
      .catch((error) => {
        if (isMounted && sessionVerificationRevisionRef.current === verificationRevision) {
          verifiedUserIdRef.current = null
          setUser(null)
          setSessionState(getFailedAuthSessionState(error))
        }
      })

    return () => {
      isMounted = false
    }
  }, [authSessionRevision, authUser])

  const activateVerifiedSession = useCallback(async (sessionId: string) => {
    const currentUser = getCurrentFirebaseUser()

    if (!currentUser || !sessionId) {
      throw new Error('Secure login session is no longer available.')
    }

    const currentSessionId = await traceLearningPerformance({
      event: 'auth.sessionClaim.activateVerified',
      task: () => getUserActiveSessionId(currentUser),
    })

    if (currentSessionId !== sessionId) {
      throw new Error('Secure login session did not include the expected session claim.')
    }

    sessionVerificationRevisionRef.current += 1
    verifiedUserIdRef.current = currentUser.uid
    setUser(currentUser)
    setSessionState(getActiveAuthSessionState())
  }, [])

  useEffect(() => {
    if (!hasFirebaseConfig() || !user) {
      return
    }

    let isMounted = true
    let unsubscribeFromActiveSession: () => void = () => undefined

    const forceSignOutForReplacedSession = async () => {
      if (!isMounted || getCurrentFirebaseUser()?.uid !== user.uid) {
        return
      }

      verifiedUserIdRef.current = null
      setSessionState(getReplacedAuthSessionState())
      setUser(null)
      await logoutCurrentUser().catch(() => undefined)

      if (isMounted) {
        setSessionState(getSignedOutAuthSessionState('signed-out-elsewhere'))
      }
    }

    const subscribeToUserActiveSession = async () => {
      const currentSessionId = await traceLearningPerformance({
        event: 'auth.sessionClaim.activeListener',
        task: () => getUserActiveSessionId(user, true),
      }).catch(() => null)

      if (!isMounted || !currentSessionId) {
        return
      }

      const subscribeStartedAt =
        typeof performance !== 'undefined' ? performance.now() : null
      unsubscribeFromActiveSession = subscribeToActiveSession(
        user.uid,
        ({ activeSessionId, isFromCache }) => {
          if (subscribeStartedAt !== null) {
            traceLearningPerformance({
              event: 'auth.activeSession.snapshot',
              getDetails: () => ({
                durationMs: Math.round(performance.now() - subscribeStartedAt),
                hasActiveSession: Boolean(activeSessionId),
                isFromCache,
              }),
              task: () => Promise.resolve(null),
            }).catch(() => undefined)
          }

          if (isFromCache || !activeSessionId) {
            return
          }

          if (activeSessionId !== currentSessionId) {
            void forceSignOutForReplacedSession()
          }
        },
        () => {
          void forceSignOutForReplacedSession()
        },
      )
    }

    void subscribeToUserActiveSession()

    return () => {
      isMounted = false
      unsubscribeFromActiveSession()
    }
  }, [user])

  const derivedState = useMemo(
    () => deriveAuthSessionState({ sessionState, user }),
    [sessionState, user],
  )

  const value = useMemo<AuthSessionContextValue>(
    () => ({
      activateVerifiedSession,
      hasConfiguredFirebase: hasFirebaseConfig(),
      isAuthenticated: derivedState.isAuthenticated,
      isResolvingSession: derivedState.isResolvingSession,
      isSigningOutForReplacedSession: derivedState.isSigningOutForReplacedSession,
      sessionSignOutReason: sessionState.signOutReason,
      sessionState,
      user,
    }),
    [activateVerifiedSession, derivedState, sessionState, user],
  )

  return (
    <AuthSessionContext.Provider value={value}>
      {children}
    </AuthSessionContext.Provider>
  )
}
